import { IKnmPreview } from 'ApiPackage/SearchModule/interfaces/IKnmPreview';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { fetchReasonTitle } from 'CorePackage/MainModule/common/fieldUtils';
import { DATE_FORMAT_LONG, DATE_FORMAT_LONG_MINUTE } from 'CorePackage/MainModule/constants/dateFormat';

import { KnmSubtitle } from './KnmSubtitle';
import styles from './styles.module.scss';
import { parseErknmValue } from '../common/parseErknmValue';
import { SubjectNotifications } from './SubjectNotifications';
import { EpguNotifications } from './EpguNotifications';
import { getSuspensionEvents } from '../common/getSuspensionEvents';
import { getPeriodExtension } from '../common/getPeriodExtension';
import { getFullName } from '../common/getFullName';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';

interface IPmCommonProps {
  passportData?: IKnmPreview;
}

export const PmCommon = ({ passportData }: IPmCommonProps) => {
  const pmTypeValue: { code?: string; title?: string } | undefined = parseErknmValue<{ code?: string }>(
    passportData?.pmErknm?.pmType?.value || '',
  );
  const isPmTypeOP = pmTypeValue?.code === 'OP';
  const startDateTitle = isPmTypeOP ? 'Дата объявления предостережения' : 'Дата начала ПМ';
  const isErp = passportData?.version === 'ERP';

  let signerPositionValue;
  if (isPmTypeOP) {
    signerPositionValue = passportData?.pmErknm?.warningContentCreatorPosition;
  } else {
    const val = passportData?.pmErknm?.signerPosition?.value;
    if (val) {
      signerPositionValue = JSON.parse(val)?.title;
    }
  }
  const signerPositionTitle = isPmTypeOP
    ? 'Должность лица, сформировавшего содержание предостережения'
    : 'Должность лица, подписавшего паспорт ПМ';
  const isShowEventSubject =
    (passportData?.pmErknm.requirements && passportData.pmErknm.requirements.length > 0) ||
    (passportData?.pmErknm.approveDocs && passportData.pmErknm.approveDocs.length > 0) ||
    (passportData?.links && passportData.links.length > 0);

  const isShowTakenMeasures = !!passportData?.pmErknm?.takenMeasures?.length;

  const suspensionEvents = getSuspensionEvents(passportData, 'pmErknm');
  const periodExtension = getPeriodExtension(passportData, 'pmErknm');

  let automaticSignatureMeta;
  if (!isPmTypeOP) {
    automaticSignatureMeta = {
      systemSignatureUsed: passportData?.pmErknm?.preventiveVisitSystemSignatureUsed,
      systemSignatureTitle: passportData?.pmErknm?.preventiveVisitSystemSignatureTitle,
      systemSignatureMnemonic: passportData?.pmErknm?.preventiveVisitSystemSignatureMnemonic,
      systemSignatureDateTime: passportData?.pmErknm?.preventiveVisitSystemSignatureDateTime,
    };
  } else {
    automaticSignatureMeta = {
      systemSignatureUsed: passportData?.pmErknm?.warningIssuingSystemSignatureUsed,
      systemSignatureTitle: passportData?.pmErknm?.warningIssuingSystemSignatureTitle,
      systemSignatureMnemonic: passportData?.pmErknm?.warningIssuingSystemSignatureMnemonic,
      systemSignatureDateTime: passportData?.pmErknm?.warningIssuingSystemSignatureDateTime,
    };
  }

  const renderFzName = (fzName: string) => {
    const splitedName = fzName.split(' ');
    if (splitedName.length > 1) return splitedName.join('-');
    return `${fzName}-ФЗ`;
  };

  return (
    <div className={styles.ColumnContent}>
      <KnmSubtitle title="Сведения о профилактическом мероприятии">
        {passportData?.erpId && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Номер электронного паспорта ПМ</div>
            <div className={styles.ColValue}>{passportData?.erpId}</div>
          </div>
        )}
        {passportData?.pmErknm && passportData?.pmErknm.pmType && passportData?.pmErknm.pmType.value && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Вид профилактического мероприятия</div>
            <div className={styles.ColValue}>{pmTypeValue?.title}</div>
          </div>
        )}
        {isErp && passportData?.pmErknm && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Федеральный закон, в соответствии с которым проводится мероприятие</div>
            <div className={styles.ColValue}>{passportData?.fzName && renderFzName(passportData?.fzName)}</div>
          </div>
        )}
        {passportData?.startDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>{startDateTitle}</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.startDate, dateFormat: DATE_FORMAT_LONG })}
            </div>
          </div>
        )}
        {!isPmTypeOP && passportData?.stopDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата окончания ПМ</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.stopDate, dateFormat: DATE_FORMAT_LONG })}
            </div>
          </div>
        )}
        {!isPmTypeOP && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Обязательный профилактический визит</div>
            <div className={styles.ColValue}>
              <Checkbox
                checked={passportData?.pmErknm.isUnableToRefuse}
                size="medium"
                type="checkbox"
                theme="system"
                disabled={true}
              />
            </div>
          </div>
        )}
        {passportData?.pmErknm && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Иное указание места (нет в ФИАС)</div>
            <div className={styles.ColValue}>
              <Checkbox
                checked={passportData?.pmErknm.notInFIAS}
                size="medium"
                type="checkbox"
                theme="system"
                disabled={true}
              />
            </div>
          </div>
        )}
        {passportData?.pmErknm && automaticSignatureMeta?.systemSignatureUsed && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Информация о подписании сведений технической подписью внешней информационной системы
            </div>
            <div className={styles.ColValue}>
              Паспорт подписан по СМЭВ{' '}
              {formatDate({
                date: automaticSignatureMeta.systemSignatureDateTime as string,
                dateFormat: DATE_FORMAT_LONG,
              })}{' '}
              в автоматическом режиме подписью системы &quot;{automaticSignatureMeta.systemSignatureTitle}&quot;
            </div>
          </div>
        )}
        {passportData?.pmErknm && (passportData?.pmErknm.location || passportData?.pmErknm.manualAddress) && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Место проведения профилактического мероприятия</div>
            <div className={styles.ColValue}>
              {passportData?.pmErknm.location || passportData?.pmErknm.manualAddress}
            </div>
          </div>
        )}
        {passportData?.pmErknm && (passportData?.pmErknm.region || passportData?.pmErknm.fiasRegion) && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Субъект РФ</div>
            <div className={styles.ColValue}>{passportData?.pmErknm.region || passportData?.pmErknm.fiasRegion}</div>
          </div>
        )}
        {(passportData?.pmErknm?.fullName || passportData?.pmErknm?.officialFullName) && (
          <div className={styles.Row}>
            <div className={styles.ColText}>{'ФИО должностного лица'}</div>
            <div className={styles.ColValue}>
              {passportData?.pmErknm?.fullName || passportData?.pmErknm?.officialFullName || '-'}
            </div>
          </div>
        )}
        {signerPositionValue && (
          <div className={styles.Row}>
            <div className={styles.ColText}>{signerPositionTitle}</div>
            <div className={styles.ColValue}>{signerPositionValue}</div>
          </div>
        )}
        {passportData?.pmErknm?.interactionCategory && (
          <KnmSubtitle
            title="Сведения об использовании в рамках ПМ средств дистанционного взаимодействия с контролируемыми лицами
"
          >
            <div className={styles.Row}>
              <div className={styles.ColText}>Категория взаимодействия</div>
              <div className={styles.ColValue}>{passportData?.pmErknm?.interactionCategory?.name}</div>
            </div>
          </KnmSubtitle>
        )}
        {passportData?.pmErknm?.videoCallInfo &&
          passportData?.pmErknm?.videoCallInfo?.map((vks, index) => (
            <div className={styles.SectionBlock} key={`video-call-info-${index}`}>
              <div className={styles.SectionTitle}>Видео-конференц-связь #{index + 1}</div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата и время проведения ВКС</div>
                <div className={styles.ColValue}>
                  {formatDate({
                    date: vks?.dateTime || '',
                    dateFormat: DATE_FORMAT_LONG_MINUTE,
                  })}
                </div>
              </div>
            </div>
          ))}
      </KnmSubtitle>
      {periodExtension != null && (
        <KnmSubtitle title="Сведения о продлении срока проведения ПМ">
          {periodExtension.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Решение о продлении № {index + 1}</span>
              {item.event && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Действие, на основе которого срок проведения ПМ продлен</div>
                  <div className={styles.ColValue}>{item.event}</div>
                </div>
              )}
              {item.decisionDateTime && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата и время издания решения</div>
                  <div className={styles.ColValue}>{item.decisionDateTime}</div>
                </div>
              )}
              {item.decisionNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер решения</div>
                  <div className={styles.ColValue}>{item.decisionNumber}</div>
                </div>
              )}
              {item.amountDays && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Продление срока проведения ПМ</div>
                  <div className={styles.ColValue}>{item.amountDays}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {suspensionEvents.length > 0 && (
        <KnmSubtitle title="Сведения о приостановлении срока проведения ПМ">
          {suspensionEvents.map((item, index) => (
            <div key={index}>
              {/*
                <div className={styles.SectionTitle}>{`Решение о приостановлении № ${item.decisionNumber}`}</div>
               */}
              <div className={styles.SectionTitle}>{'Решение о приостановлении'}</div>
              {item.event && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Действие, на основе которого срок проведения ПМ приостановлен</div>
                  <div className={styles.ColValue}>{item.event}</div>
                </div>
              )}
              {item.decisionDateTime && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата и время издания решения</div>
                  <div className={styles.ColValue}>{item.decisionDateTime}</div>
                </div>
              )}
              {/*
              {item.decisionNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер решения</div>
                  <div className={styles.ColValue}>{item.decisionNumber}</div>
                </div>
              )}
              */}
              {item.startDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата начала приостановления срока ПМ</div>
                  <div className={styles.ColValue}>{item.startDate}</div>
                </div>
              )}
              {item.stopDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата окончания приостановления срока ПМ</div>
                  <div className={styles.ColValue}>{item.stopDate}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData?.pmErknm && passportData?.pmErknm.noteWarning && (
        <KnmSubtitle title="Сведения о содержании предостережения">
          {passportData?.erpId && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Описание предостережения</div>
              <div className={styles.ColValue}>{passportData?.pmErknm.noteWarning}</div>
            </div>
          )}
        </KnmSubtitle>
      )}

      {passportData?.pmErknm?.rescheduling &&
        passportData?.pmErknm?.rescheduling.map((rescheduling, i) => {
          return (
            <KnmSubtitle
              key={i}
              title={`Перенос проведения мероприятия от ${formatDate({
                date: rescheduling.initiationDate,
                dateFormat: DATE_FORMAT_LONG,
              })}`}
            >
              <div className={styles.Row}>
                <div className={styles.ColText}>Новая дата начала КНМ</div>
                <div className={styles.ColValue}>
                  {formatDate({ date: rescheduling.newStartDate, dateFormat: DATE_FORMAT_LONG })}
                </div>
              </div>
              {rescheduling.newEndDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Новая дата окончания КНМ</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: rescheduling.newEndDate, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {rescheduling.comment && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Комментарий</div>
                  <div className={styles.ColValue}>{rescheduling.comment}</div>
                </div>
              )}
            </KnmSubtitle>
          );
        })}

      {passportData?.pmErknm?.publicAbrogation && (
        <KnmSubtitle title="Сведения о нормативно-правовых основаниях, повлекших за собой необходимость в запуске процесса отмены проведения мероприятий">
          <div className={styles.Row}>
            <div className={styles.ColText}>Основания отмены мероприятий</div>
            <div className={styles.ColValue}>{passportData?.pmErknm?.publicAbrogation.reason}</div>
          </div>
          <div className={styles.Row}>
            <div className={styles.ColText}>Комментарий</div>
            <div className={styles.ColValue}>{passportData?.pmErknm?.publicAbrogation.comment || '-'}</div>
          </div>
        </KnmSubtitle>
      )}

      {passportData?.massCancel?.cancelled && (
        <KnmSubtitle title="Сведения о нормативно-правовых основаниях, повлекших за собой необходимость в запуске процесса отмены проведения мероприятий">
          <div className={styles.Row}>
            <div className={styles.ColText}>Основание массовой отмены</div>
            <div className={styles.ColValue}>{passportData.massCancel.reason}</div>
          </div>
          {passportData.massCancel.reasonFileName && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Файл основания массовой отмены</div>
              <div className={styles.ColValue}>
                <a
                  href={`/public/api/inspections/${passportData.id}/mass-cancel-reason-file`}
                  download={passportData.massCancel.reasonFileName}
                >
                  {passportData.massCancel.reasonFileName}
                </a>
              </div>
            </div>
          )}
        </KnmSubtitle>
      )}

      {passportData?.pmErknm?.reasons && passportData?.pmErknm.reasons.length > 0 && (
        <KnmSubtitle title="Основания проведения профилактических мероприятий">
          {passportData?.pmErknm.reasons.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Основание проведения ПМ #{index + 1}</span>
              {item.reason && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{fetchReasonTitle(item, isErp)}</div>
                </div>
              )}
              {item.reason.programNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер программы проверок</div>
                  <div className={styles.ColValue}>{item.reason.programNumber}</div>
                </div>
              )}
              {item.riskIndicators?.map((indicatorValue, indicatorIndex) => {
                return (
                  <>
                    {indicatorValue.riskIndicator && (
                      <div key={indicatorIndex}>
                        <span className={styles.SectionTitle}>Индикатор риска #{indicatorIndex + 1}</span>
                        <div className={styles.Row}>
                          <div className={styles.ColText}>
                            {parseErknmValue<{ title?: string }>(indicatorValue.riskIndicator.value)?.title}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })}
              {item.order && (
                <>
                  {item.order.guarantors.map((guarantor, guarantorIndex) => {
                    return (
                      <div className={styles.Row} key={guarantorIndex}>
                        <div className={styles.ColText}>ФИО лица, издавшего поручение</div>
                        <div className={styles.ColValue}>{getFullName(guarantor)}</div>
                      </div>
                    );
                  })}
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Номер поручения</div>
                    <div className={styles.ColValue}>{item.order.number}</div>
                  </div>
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Дата поручения</div>
                    <div className={styles.ColValue}>
                      {formatDate({
                        date: item.order.date,
                        dateFormat: DATE_FORMAT_LONG,
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {isShowEventSubject && (
        <KnmSubtitle title="Предмет профилактического мероприятия">
          {passportData?.pmErknm.requirements?.map((npa, index) => (
            <div className={styles.SectionBlock} key={index}>
              <div className={styles.SectionTitle}>Обязательные требования, подлежащие проверке #{index + 1}</div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Нормативно-правовой акт</div>
                <div className={styles.ColValue}>{npa.title}</div>
              </div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Номер</div>
                <div className={styles.ColValue}>{npa.number}</div>
              </div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата утверждения</div>
                <div className={styles.ColValue}>
                  {formatDate({ date: npa.acceptanceDate, dateFormat: DATE_FORMAT_LONG })}
                </div>
              </div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Структурная единица</div>
                <div className={styles.ColValue}>{npa.structuralUnit.title}</div>
              </div>
              {npa.structuralUnit.mandatoryRequirements.map((requirement, index1) => (
                <div key={index1} className={styles.Row}>
                  <div className={styles.ColText}>Содержание требования #{index1 + 1}</div>
                  <div className={styles.ColValue}>{requirement.title}</div>
                </div>
              ))}
            </div>
          ))}
          {passportData?.links && passportData.links.length > 0 && (
            <KnmSubtitle title="Ссылка на другой паспорт КНМ или ПМ">
              {passportData.links.map((item, index) => (
                <div className={styles.SectionBlock} key={index}>
                  {item.erpId && (
                    <>
                      <div className={styles.SectionTitle}>Ссылка #{index + 1}</div>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Номер КНМ или ПМ</div>
                        <div className={styles.ColValue}>{item.erpId}</div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </KnmSubtitle>
          )}
        </KnmSubtitle>
      )}
      {isShowTakenMeasures && (
        <KnmSubtitle title="Предложенные меры">
          {passportData.pmErknm.takenMeasures?.map((tm, index) => (
            <div className={styles.SectionBlock} key={index}>
              <div className={styles.Row}>
                <div className={styles.ColText}>Мера №{index + 1}</div>
                <div className={styles.ColValue}>{tm.value}</div>
              </div>
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData?.pmErknm.docTarget && passportData.pmErknm.docTarget.length > 0 && (
        <KnmSubtitle title="Разрешительные и иные документы">
          {passportData?.pmErknm.docTarget.map((docTarget, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Разрешительный или иной документ #{index + 1}</span>
              <div className={styles.Row}>
                <div className={styles.ColText}>Категория документа</div>
                <div className={styles.ColValue}>
                  {docTarget.ervkControlKindDocTarget.type === 'PERMITTED'
                    ? 'Разрешительный документ'
                    : 'Иной документ'}
                </div>
              </div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Документ</div>
                <div className={styles.ColValue}>{docTarget.ervkControlKindDocTarget.targetTitle || ''}</div>
              </div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Конкретное проверяемое требование и пункт</div>
                <div className={styles.ColValue}>{docTarget.specificRequirementAndClause || ''}</div>
              </div>
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData?.pmErknm.approveDocs && passportData.pmErknm.approveDocs.length > 0 && (
        <KnmSubtitle title="Разрешительные и иные документы">
          {passportData?.pmErknm.approveDocs.map((item, index) => (
            <div className={styles.SectionBlock} key={index}>
              {item.approveDoc && item.approveDoc.value && (
                <>
                  <div className={styles.SectionTitle}>Документ #{index + 1}</div>
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Наименование документа</div>
                    <div className={styles.ColValue}>
                      {parseErknmValue<{ title?: string }>(item.approveDoc.value)?.title}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      <SubjectNotifications subjectNotifications={passportData?.subjectNotifications} isPm />
      <EpguNotifications epguNotifications={passportData?.epguNotifications} />
      {passportData?.pmErknm?.pmRejects && passportData.pmErknm.pmRejects.length !== 0 && (
        <KnmSubtitle title="Информация о непроведении (несогласии) ПМ">
          {passportData.pmErknm.pmRejects.map((item, index) => (
            <div key={index}>
              <div className={styles.SectionTitle}>Причина #{index + 1}</div>
              {item.pmCanceledReason?.name && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Причина непроведения профилактического мероприятия</div>
                  <div className={styles.ColValue}>{item.pmCanceledReason.name}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
    </div>
  );
};
